import { useState, type ChangeEvent } from 'react'
import { Button, EditIcon, Label, Paragraph, Select } from '@quickmd-llc/design-system'
import { pluralize } from '@quickmd-llc/frontend-utils'

import {
  THROTTLING_PRESET_DESCRIPTIONS,
  THROTTLING_PRESET_LABELS,
  THROTTLING_PRESET_OPTIONS,
  fromThrottlingPreset,
  isThrottlingMootForDuration,
  toThrottlingPreset,
  type ThrottlingPreset,
  type ThrottlingPresetOrCustom,
} from './newPatientThrottling'

// Match the legacy visit-length dropdown: 1-min steps from 5 to 15, then 5-min steps to 60.
// (Mirrors `visitLengthDropdownOption` in src/utils/common.js.)
const DURATION_OPTIONS = (() => {
  const out: number[] = []
  for (let i = 5; i <= 60; i += i < 15 ? 1 : 5) {
    out.push(i)
  }
  return out
})()

// Sentinel for the duration select's "no override" row. Saving with this selected sends
// `Duration: null`, so a throttling-only edit never pins the provider to a duration they
// never had — the visit type's own default keeps applying, even if it later changes.
const USE_DEFAULT_DURATION = ''

type SaveArgs = {
  duration: number | null
  isExemptFromNewPatientSpacing: boolean
  maxConsecutiveNewPatientVisits: number | null
}

interface ProductivityCardProps {
  visitTypeName: string
  serviceName: string
  duration: number | null
  defaultDuration: number | null
  isExemptFromNewPatientSpacing: boolean
  maxConsecutiveNewPatientVisits: number | null
  /**
   * Whether this visit type opts into new-patient spacing (the per-visit-type toggle on the
   * service type). The backend skips the spacing check entirely when it is off, so the throttling
   * control renders disabled with an explanation rather than hidden — hiding it left VAs hunting
   * for a control that simply wasn't there. This is also how the MOUD-only scope is enforced: the
   * toggle is turned on for MOUD visit types only.
   */
  isNewPatientSpacingEnabled: boolean
  isEditing: boolean
  onEdit: () => void
  onSave: (args: SaveArgs) => void
  onCancel: () => void
  onReset: () => void
  isSaving?: boolean
}

const formatMinutes = (minutes: number | null) =>
  minutes !== null ? `${minutes} ${pluralize('minute', minutes)}` : '—'

export function ProductivityCard({
  visitTypeName,
  serviceName,
  duration,
  defaultDuration,
  isExemptFromNewPatientSpacing,
  maxConsecutiveNewPatientVisits,
  isNewPatientSpacingEnabled,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onReset,
  isSaving,
}: ProductivityCardProps) {
  const savedPreset: ThrottlingPresetOrCustom = toThrottlingPreset({
    isExempt: isExemptFromNewPatientSpacing,
    maxConsecutive: maxConsecutiveNewPatientVisits,
  })

  const [editDuration, setEditDuration] = useState<number | null>(duration)
  const [editPreset, setEditPreset] = useState<ThrottlingPresetOrCustom>(savedPreset)

  // A duration override and a throttling override are independent — either one alone means the
  // provider has something to reset. A null duration is an override row that leaves the length
  // alone (throttling-only), so it is NOT a duration adjustment.
  const isDurationOverridden = duration !== null && duration !== defaultDuration
  const isOverridden = isDurationOverridden || savedPreset !== 'default'

  // An unset duration falls back to the visit type's own, which is what spacing is judged against.
  const effectiveEditDuration = editDuration ?? defaultDuration
  const wasMoot = isThrottlingMootForDuration(duration ?? defaultDuration)
  const willBeMoot = isThrottlingMootForDuration(effectiveEditDuration)

  if (isEditing) {
    const isThrottlingSelected = editPreset !== 'off' && editPreset !== 'custom'

    return (
      <div className='flex flex-col gap-sp-2 rounded-lg border border-shade-10 bg-white p-sp-2'>
        <p className='text-base font-semibold tracking-[0.12px] text-text-dark'>{visitTypeName}</p>
        <p className='text-sm tracking-[0.12px] text-text-medium'>
          {serviceName}: {formatMinutes(defaultDuration)} (default)
        </p>
        <div className='flex flex-col gap-sp-0.5'>
          <Label size='md'>Appointment length</Label>
          <Select
            aria-label='Appointment length'
            value={editDuration === null ? USE_DEFAULT_DURATION : String(editDuration)}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              const raw = e.target.value
              setEditDuration(raw === USE_DEFAULT_DURATION ? null : Number(raw))
            }}>
            <option value={USE_DEFAULT_DURATION}>Use default ({formatMinutes(defaultDuration)})</option>
            {DURATION_OPTIONS.map((d) => (
              <option key={d} value={String(d)}>
                {formatMinutes(d)}
              </option>
            ))}
          </Select>
        </div>
        <div className='flex flex-col gap-sp-0.5'>
          {/* exactOptionalPropertyTypes: never hand an optional prop an explicit undefined. */}
          <Label size='md' {...(isNewPatientSpacingEnabled ? {} : { className: 'text-text-light' })}>
            New patient throttling
          </Label>
          <Select
            aria-label='New patient throttling'
            disabled={!isNewPatientSpacingEnabled}
            value={editPreset}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              const next = e.target.value
              if (isThrottlingPreset(next)) setEditPreset(next)
            }}>
            {editPreset === 'custom' && (
              <option value='custom' disabled>
                {THROTTLING_PRESET_LABELS.custom}
              </option>
            )}
            {THROTTLING_PRESET_OPTIONS.map((preset) => (
              <option key={preset} value={preset}>
                {THROTTLING_PRESET_LABELS[preset]}
              </option>
            ))}
          </Select>
          {isNewPatientSpacingEnabled ?
            <>
              <Paragraph size='xs' className='text-text-medium'>
                {THROTTLING_PRESET_DESCRIPTIONS[editPreset]}
              </Paragraph>
              {/* Names no threshold. The length at which spacing starts applying is backend config
                  admin cannot read, so stating it would be a claim that goes stale silently. The
                  constant still decides WHEN this fires; it just no longer appears in the copy. */}
              {!willBeMoot && wasMoot && isThrottlingSelected ?
                <Paragraph size='xs' role='status' className='text-text-highlight'>
                  Shortening this appointment brings this provider into new patient throttling.
                </Paragraph>
              : null}
            </>
          : <Paragraph size='xs' className='text-text-light'>
              New patient spacing isn&rsquo;t enabled for this visit type. Turn it on under Management &rsaquo; Service
              types to set throttling here.
            </Paragraph>
          }
        </div>
        <div className='flex items-center gap-sp-1'>
          {isOverridden ?
            <Button priority='tertiary' size='s' onClick={onReset}>
              Reset
            </Button>
          : null}
          <div className='flex-1' />
          <Button priority='tertiary' size='s' onClick={onCancel}>
            Cancel
          </Button>
          <Button
            priority='primary'
            size='s'
            onClick={() =>
              onSave({
                duration: editDuration,
                // Leave the stored values untouched when they aren't editable here: 'custom' is
                // display-only, and a visit type that doesn't opt into spacing shows the control
                // disabled purely so the VA can see why it isn't settable.
                ...(isNewPatientSpacingEnabled && isThrottlingPreset(editPreset) ?
                  fromThrottlingPreset(editPreset)
                : {
                    isExemptFromNewPatientSpacing,
                    maxConsecutiveNewPatientVisits,
                  }),
              })
            }
            disabled={isSaving ?? false}>
            Save
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className='flex items-start gap-sp-2 rounded-lg border border-shade-10 bg-white p-sp-2'>
      <div className='flex flex-1 flex-col gap-sp-1'>
        <p className='text-base font-semibold tracking-[0.12px] text-text-dark'>{visitTypeName}</p>
        <p className='text-sm tracking-[0.12px] text-text-medium'>
          {serviceName}: {formatMinutes(duration ?? defaultDuration)}
          {isDurationOverridden ?
            <span className='text-text-highlight'> (adjusted)</span>
          : null}
        </p>
        {/* Reports the setting that is SAVED, not the one currently in force — a provider whose
            appointment length exempts them still shows their stored preset. Whether it is being
            enacted is deliberately not stated, because saying so would mean naming the threshold
            that decides it, and admin does not state that threshold anywhere. */}
        {isNewPatientSpacingEnabled ?
          <p className='text-sm tracking-[0.12px] text-text-medium'>
            New patient throttling: {THROTTLING_PRESET_LABELS[savedPreset]}
          </p>
        : null}
      </div>
      {/* Names the visit type because the grid renders one of these per card, and says
          "settings" rather than "duration" because the form now edits throttling too. */}
      <Button priority='tertiary' size='s' onClick={onEdit} aria-label={`Edit productivity for ${visitTypeName}`}>
        <EditIcon iconSize='s' />
      </Button>
    </div>
  )
}

function isThrottlingPreset(value: string): value is ThrottlingPreset {
  return THROTTLING_PRESET_OPTIONS.some((preset) => preset === value)
}
